import { getAuthContext } from '../../context/AuthContext';

const getAxios = () => getAuthContext().axiosInstance;

/**
 * Servicio para crear ventas/pedidos desde el e-commerce
 * Conecta con VentaController del backend
 * 
 * Nota: Este servicio REQUIERE autenticación (JWT token)
 * Se usa cuando el cliente crea una orden desde el e-commerce
 */
export const EcommerceVentaService = {
    /**
     * Crea una venta/pedido completamente (transacción atómica)
     * Incluye validación de stock, generación de kardex, etc.
     * 
     * @param {Object} datosVenta - Objeto con estructura VentaRequestDTO
     * @returns {Promise<Object>} Venta creada con ID y código de venta
     * 
     * Estructura esperada:
     * {
     *   idEmpresa: 1,
     *   idSucursal: 1,
     *   idUsuario: (ID del usuario autenticado o cliente),
     *   idCliente: (ID del cliente registrado),
     *   estado: 'completada',
     *   tipoDePago: 'efectivo|tarjeta|credito|mixto',
     *   tipoComprobante: 'boleta|factura',
     *   pagoCon: 100.00,
     *   vuelto: 10.00,
     *   efectivo: 50.00,
     *   tarjeta: 50.00,
     *   credito: 0,
     *   montoTotal: 90.00,
     *   subTotal: 75.76,
     *   detalles: [
     *     {
     *       idVariante: 5,
     *       cantidad: 2,
     *       precioUnitario: 37.88,
     *       subTotal: 75.76
     *     }
     *   ]
     * }
     */
    crearVentaCompleta: async (datosVenta) => {
        try {
            const response = await getAxios().post(
                '/api/ventas/transaccion',
                datosVenta
            );

            if (response.data?.success && response.data?.data) {
                return response.data.data;
            }
            throw new Error(response.data?.message || 'Error al crear la venta');
        } catch (error) {
            console.error('Error al crear venta:', error);
            throw error;
        }
    },

    /**
     * Crea una venta simple (sin validación completa)
     * Úsalo solo si crearVentaCompleta no está disponible
     * 
     * @param {Object} venta - Objeto básico Venta
     * @returns {Promise<Object>} Venta creada
     */
    crearVenta: async (venta) => {
        try {
            const response = await getAxios().post('/api/ventas', venta);

            if (response.data?.success && response.data?.data) {
                return response.data.data;
            }
            throw new Error(response.data?.message || 'Error al crear la venta');
        } catch (error) {
            console.error('Error al crear venta:', error);
            throw error;
        }
    },

    /**
     * Obtiene todas las ventas del usuario autenticado
     * Requiere autenticación
     * 
     * @returns {Promise<Array>} Lista de ventas
     */
    obtenerVentas: async () => {
        try {
            const response = await getAxios().get('/api/ventas');

            if (response.data?.success && response.data?.data) {
                return response.data.data;
            }
            return [];
        } catch (error) {
            console.error('Error al obtener ventas:', error);
            throw error;
        }
    },

    /**
     * Obtiene una venta por su código único
     * Requiere autenticación
     * 
     * @param {string} codigoVenta - Código único de la venta
     * @returns {Promise<Object>} Datos de la venta
     */
    obtenerVentaPorCodigo: async (codigoVenta) => {
        try {
            const response = await getAxios().get(
                `/api/ventas/codigo/${encodeURIComponent(codigoVenta)}`
            );

            if (response.data?.success && response.data?.data) {
                return response.data.data;
            }
            return null;
        } catch (error) {
            console.error('Error al obtener venta:', error);
            throw error;
        }
    },

    /**
     * Obtiene ventas de un usuario específico
     * Requiere autenticación
     * 
     * @param {number} idUsuario - ID del usuario
     * @returns {Promise<Array>} Lista de ventas del usuario
     */
    obtenerVentasPorUsuario: async (idUsuario) => {
        try {
            const response = await getAxios().get(
                `/api/ventas/usuario/${idUsuario}`
            );

            if (response.data?.success && response.data?.data) {
                return response.data.data;
            }
            return [];
        } catch (error) {
            console.error('Error al obtener ventas:', error);
            throw error;
        }
    },

    /**
     * Obtiene ventas de una sucursal
     * Requiere autenticación
     * 
     * @param {number} idSucursal - ID de la sucursal
     * @returns {Promise<Array>} Lista de ventas
     */
    obtenerVentasPorSucursal: async (idSucursal) => {
        try {
            const response = await getAxios().get(
                `/api/ventas/sucursal/${idSucursal}`
            );

            if (response.data?.success && response.data?.data) {
                return response.data.data;
            }
            return [];
        } catch (error) {
            console.error('Error al obtener ventas:', error);
            throw error;
        }
    }
};
